/**
 * A crown health screen: every crown of an inventory coloured by its category (dead, declining,
 * threatened, healthy, or what the screen could not judge) or by its stress z-score, the hotspots
 * of dead, snag and declining crowns outlined, over the RGB orthophoto or an index of the multispectral
 * orthophoto the screen read; below, the counts per category and per genus.
 *
 * The map draws from the screen's tile archive (`crown_health.pmtiles` beside its GeoPackage) by
 * the photo inventory's own builder: points below z16, crown polygons from it, the genus filter,
 * the popup and the highlight of the clicked crown. The figures come from `crown_health.json`.
 */
import { useMemo, useState } from 'react';
import { Group, ScrollArea, SimpleGrid, Stack, Table, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@/components/EmptyState';
import { StatCard } from '@/components/StatCard';
import { useFormat } from '@/components/useFormat';
import { useGenusName } from '@/map/genusName';
import { LayerSample } from '@/map/LayerSample';
import { genusOf, HEALTH_CATEGORIES, HEALTH_COLOR_BY, HEALTH_HOTSPOTS, type HealthCategory, type LayerOptions, type LayerRef } from '@/map/layers';
import type { BackdropChoice } from '@/map/LayerPanel';
import { MapView } from '@/map/MapView';
import { availableBands, availableDisplays, indexStops, isIndex, type BandRole } from '@/map/spectral';
import { useRasterMeta } from '@/map/useRasterMeta';
import { CROWN_HEALTH } from '@/theme/colors';
import { brand } from '@/theme/theme';
import { crownHealthConfig, CROWN_HEALTH_SECTIONS } from './crownHealth/config';
import { healthTiles, spectralOf, SPECTRAL_BACKDROPS, type Backdrop } from './crownHealth/map';
import { categoryCounts, CROWN_HEALTH_GPKG, type CrownHealthSummary } from './crownHealth/summary';
import { useHealthArchive, useHealthSummary } from './crownHealth/useCrownHealthData';
import { MapFrame } from './MapFrame';
import { bandLabel } from './multispectral/bandLabel';
import { DEFAULT_STRETCH } from './multispectral/config';
import type { KindReportProps } from './ReportPage';
import { Section } from './trees/Section';

/** The categories the screen judged (a snag by the segmentation's class); the rest are summed as "not judged" in the genus table. */
const SCREENED: HealthCategory[] = ['dead', 'snag', 'declining', 'threatened', 'healthy'];
/** The hotspots listed under the map. */
const TOP_HOTSPOTS = 10;

export function CrownHealthReport({ refs, linkSource, options, onOptionsChange, tokenSource, config }: KindReportProps) {
  const { t } = useTranslation('reports');
  const cfg = useMemo(() => crownHealthConfig(config), [config]);
  const screen = refs.find((r) => r.object_kind === 'crown_health' && r.via === 'link');
  const ortho = refs.find((r) => r.object_kind === 'orthophoto' && r.via === 'tiler');
  const ms = refs.find((r) => r.object_kind === 'multispectral_orthophoto' && r.via === 'tiler');
  const boundaries = useMemo(() => refs.filter((r) => r.object_kind === 'boundary' && r.via === 'link'), [refs]);
  const msList = useMemo(() => (ms ? [ms] : []), [ms]);
  const { metas } = useRasterMeta(msList, tokenSource);
  const meta = ms ? metas[ms.object] : undefined;

  const [screenObject, screenPath] = [screen?.object, screen?.path];
  const screenFile = useMemo(() => (screenObject ? { object: screenObject, path: screenPath || CROWN_HEALTH_GPKG } : undefined), [screenObject, screenPath]);
  const archive = useHealthArchive(screenFile, linkSource);
  const summary = useHealthSummary(screenObject, linkSource);

  // Until the tiler says which bands the file has, every view is offered; it falls back if one is not.
  const spectral = ms ? (meta ? availableDisplays(meta) : SPECTRAL_BACKDROPS).filter((d) => SPECTRAL_BACKDROPS.includes(d)) : [];
  const backdrops: Backdrop[] = [...(ortho ? (['ortho'] as Backdrop[]) : []), ...spectral];
  const [picked, setPicked] = useState<Backdrop | null>(null);
  const backdrop = picked && backdrops.includes(picked) ? picked : backdrops[0];
  const backdropRef = backdrop === 'ortho' ? ortho : backdrop ? ms : undefined;
  // One band of the multispectral orthophoto: near infrared unless picked, the first the file has if it lacks it.
  const bands = availableBands(meta);
  const [pickedBand, setPickedBand] = useState<BandRole>('nir');
  const band = !meta || bands.includes(pickedBand) ? pickedBand : (bands[0] ?? pickedBand);

  const ready = archive.state === 'ready' ? archive.archive : null;
  const mapRefs = useMemo(() => {
    const out: LayerRef[] = [...boundaries];
    if (screen && ready) {
      const tiles = healthTiles(screen.object, ready.header.maxZoom);
      out.push({ ...screen, object: `${screen.object}:${HEALTH_HOTSPOTS}`, table: HEALTH_HOTSPOTS, tiles });
      out.push({ ...screen, tiles });
    }
    if (backdropRef) out.push(backdropRef);
    return out;
  }, [boundaries, screen, ready, backdropRef]);
  // The screen comes from its archive; a boundary is read by the map as on every page.
  const drawnFromTiles = useMemo(() => (screenObject ? [screenObject, `${screenObject}:${HEALTH_HOTSPOTS}`] : []), [screenObject]);
  const focus = useMemo<[number, number, number, number] | null>(() => (ready ? [ready.header.minLon, ready.header.minLat, ready.header.maxLon, ready.header.maxLat] : null), [ready]);

  const counts = summary.state === 'ready' ? categoryCounts(summary.summary) : undefined;
  const colorBy = options.colorBy && HEALTH_COLOR_BY.includes(options.colorBy) ? options.colorBy : cfg.colorBy;
  const view = backdrop ? spectralOf(backdrop, band) : undefined;
  const withDefaults: LayerOptions = { ...options, colorBy, ...(view ? { spectral: view } : {}), ...(counts ? { healthCounts: counts } : {}) };
  // The backdrop and the counts are the page's, not part of the view a viewer's browser keeps.
  const change = (next: LayerOptions) => {
    const rest = { ...next };
    delete rest.spectral;
    delete rest.healthCounts;
    onOptionsChange(rest);
  };
  // What lies under the crowns is switched in the map's layer panel, right above the rasters.
  const backdropChoice: BackdropChoice | undefined = backdrop
    ? {
        title: t('crownHealth.backdrop'),
        choices: backdrops.map((b) => ({ value: b, label: t(`crownHealth.backdrops.${b}`) })),
        value: backdrop,
        onChange: (v) => setPicked(v as Backdrop),
        detail:
          backdrop === 'band'
            ? { label: t('multispectral.band'), choices: (bands.length ? bands : [band]).map((b) => ({ value: b, label: bandLabel(t, meta, b) })), value: band, onChange: (v) => setPickedBand(v as BandRole) }
            : undefined,
        scale:
          backdrop !== 'ortho' && isIndex(backdrop)
            ? { id: `index-${backdrop}`, label: `layers.indices.${backdrop}`, sample: { kind: 'ramp', colors: [] }, stops: indexStops(DEFAULT_STRETCH[backdrop]), hint: `layers.formulas.${backdrop}` }
            : undefined,
      }
    : undefined;

  const s = cfg.sections;
  if (!CROWN_HEALTH_SECTIONS.some((k) => s[k].enabled)) return <EmptyState title={t('crownHealth.nothingEnabled')} />;
  if (!screen) return <EmptyState title={t('crownHealth.unavailable')} />;

  return (
    <Stack gap="xl">
      {s.map.enabled && (
        <Stack gap="sm">
          {archive.state === 'none' && <EmptyState title={t('crownHealth.noTiles')} />}
          <MapFrame testId="report-crown-health" wide={!!options.wide} onWideChange={(wide) => change({ ...withDefaults, wide })}>
            <MapView refs={mapRefs} linkSource={linkSource} externalVectors={drawnFromTiles} options={withDefaults} onOptionsChange={change} tokenSource={tokenSource} focus={focus} backdrop={backdropChoice} />
          </MapFrame>
        </Stack>
      )}
      {(s.summary.enabled || s.genera.enabled) && summary.state === 'failed' && (
        <Text fz="sm" c={brand.textMuted} data-testid="ch-summary-failed">
          {t('crownHealth.summaryUnavailable')}
        </Text>
      )}
      {s.summary.enabled && summary.state === 'ready' && <SummarySection summary={summary.summary} />}
      {s.genera.enabled && summary.state === 'ready' && summary.summary.genera.length > 0 && <GeneraSection summary={summary.summary} />}
    </Stack>
  );
}

/** A category's swatch and name. */
function CategoryLabel({ category }: { category: HealthCategory }) {
  const { t } = useTranslation('viewer');
  return (
    <Group gap={6} wrap="nowrap">
      <LayerSample sample={{ kind: 'swatch', color: CROWN_HEALTH[category] }} />
      <Text fz="sm">{t(`legend.health.${category}`)}</Text>
    </Group>
  );
}

/** The key figures, the crowns per category and the largest hotspots. */
function SummarySection({ summary }: { summary: CrownHealthSummary }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const crowns = (c: HealthCategory) => summary.categories[c]?.crowns ?? 0;
  const hotspots = summary.hotspots;
  return (
    <Section title={t('crownHealth.sections.summary')}>
      <Stack gap="md">
        <SimpleGrid cols={{ base: 1, xs: 2, md: 5 }} spacing="md">
          <StatCard label={t('crownHealth.kpi.screened')} value={number(summary.screened ?? 0)} sub={summary.crowns !== null ? t('crownHealth.kpi.ofCrowns', { count: summary.crowns, formatted: number(summary.crowns) }) : undefined} accent testId="ch-stat-screened" />
          <StatCard label={t('crownHealth.kpi.dead')} value={number(crowns('dead'))} testId="ch-stat-dead" />
          <StatCard label={t('crownHealth.kpi.snag')} value={number(crowns('snag'))} testId="ch-stat-snag" />
          <StatCard label={t('crownHealth.kpi.declining')} value={number(crowns('declining'))} testId="ch-stat-declining" />
          <StatCard
            label={t('crownHealth.kpi.hotspots')}
            value={number(hotspots?.count ?? 0)}
            sub={hotspots?.areaM2 !== null && hotspots?.areaM2 !== undefined ? t('crownHealth.kpi.hotspotArea', { area: number(hotspots.areaM2 / 10000, 2) }) : undefined}
            testId="ch-stat-hotspots"
          />
        </SimpleGrid>
        <ScrollArea type="auto">
          <Table striped withTableBorder data-testid="ch-categories">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('crownHealth.columns.category')}</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>{t('crownHealth.columns.crowns')}</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>{t('crownHealth.columns.area')}</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>{t('crownHealth.columns.volume')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {HEALTH_CATEGORIES.filter((c) => summary.categories[c]).map((c) => {
                const f = summary.categories[c]!;
                return (
                  <Table.Tr key={c} data-testid={`ch-category-${c}`}>
                    <Table.Td>
                      <CategoryLabel category={c} />
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{number(f.crowns)}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{f.areaM2 === null ? '—' : number(f.areaM2)}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{f.volumeM3 === null ? '—' : number(f.volumeM3, 1)}</Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        {hotspots && hotspots.top.length > 0 && <HotspotsTable hotspots={hotspots.top.slice(0, TOP_HOTSPOTS)} />}
        <Text fz="xs" c={brand.textMuted}>
          {t('crownHealth.method', {
            radius: number(summary.params.neighbourRadiusM),
            ndvi: number(summary.params.deadNdvi, 2),
            z: number(summary.params.declineZ, 1),
            threat: number(summary.params.threatRadiusM),
            min: summary.params.hotspotMinCrowns,
            eps: number(summary.params.hotspotEpsM),
          })}
        </Text>
      </Stack>
    </Section>
  );
}

function HotspotsTable({ hotspots }: { hotspots: NonNullable<CrownHealthSummary['hotspots']>['top'] }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  return (
    <Stack gap={4}>
      <Text fz="sm" fw={600}>
        {t('crownHealth.hotspotsTitle')}
      </Text>
      <ScrollArea type="auto">
        <Table striped withTableBorder data-testid="ch-hotspots">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('crownHealth.columns.hotspot')}</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>{t('crownHealth.columns.dead')}</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>{t('crownHealth.columns.snag')}</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>{t('crownHealth.columns.declining')}</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>{t('crownHealth.columns.threatened')}</Table.Th>
              <Table.Th>{t('crownHealth.columns.genera')}</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>{t('crownHealth.columns.area')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {hotspots.map((h) => (
              <Table.Tr key={h.hotspot} data-testid={`ch-hotspot-${h.hotspot}`}>
                <Table.Td>{h.hotspot}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{number(h.dead)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{number(h.snag)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{number(h.declining)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{number(h.threatened)}</Table.Td>
                <Table.Td>{h.genera ?? '—'}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{h.areaM2 === null ? '—' : number(h.areaM2)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Stack>
  );
}

/** The crowns of each genus by category: the screened ones in columns, what was not judged summed. */
function GeneraSection({ summary }: { summary: CrownHealthSummary }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const genus = useGenusName();
  return (
    <Section title={t('crownHealth.sections.genera')}>
      <ScrollArea type="auto">
        <Table striped withTableBorder data-testid="ch-genera">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('crownHealth.columns.genus')}</Table.Th>
              {SCREENED.map((c) => (
                <Table.Th key={c} style={{ textAlign: 'right' }}>
                  <Group gap={4} justify="flex-end" wrap="nowrap">
                    <LayerSample sample={{ kind: 'swatch', color: CROWN_HEALTH[c] }} />
                    {t(`crownHealth.columns.${c}`)}
                  </Group>
                </Table.Th>
              ))}
              <Table.Th style={{ textAlign: 'right' }}>{t('crownHealth.columns.notJudged')}</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>{t('crownHealth.columns.crowns')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {summary.genera.map((row) => {
              const screened = SCREENED.reduce((sum, c) => sum + (row.categories[c] ?? 0), 0);
              return (
                <Table.Tr key={row.genus} data-testid={`ch-genus-${row.genus}`}>
                  <Table.Td>{genus(genusOf(row.genus))}</Table.Td>
                  {SCREENED.map((c) => (
                    <Table.Td key={c} style={{ textAlign: 'right' }}>
                      {row.categories[c] ? number(row.categories[c]) : '—'}
                    </Table.Td>
                  ))}
                  <Table.Td style={{ textAlign: 'right' }}>{row.crowns - screened > 0 ? number(row.crowns - screened) : '—'}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{number(row.crowns)}</Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Section>
  );
}
